import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  Bell, 
  ChevronDown, 
  User, 
  LogOut, 
  FileText, 
  ExternalLink,
  Menu,
  LifeBuoy,
  HelpCircle,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { signOut } from 'firebase/auth';
import { auth } from '../config/firebase';
import { useAuthStore } from '../store/authStore';
import { useGuideStore } from '../store/guideStore';
import NotificationCenter from './NotificationCenter';
import UserSettings from './UserSettings';
import Logo from './Logo';
import toast from 'react-hot-toast';
import { useMediaQuery } from '../hooks/useMediaQuery';
import SupportModal from './SupportModal';

interface NavbarProps {
  isDropdownOpen: boolean;
  setIsDropdownOpen: (isOpen: boolean) => void;
  onMobileMenuClick: () => void;
  isSidebarCollapsed?: boolean;
  onToggleSidebar?: () => void;
}

const Navbar: React.FC<NavbarProps> = ({ 
  isDropdownOpen, 
  setIsDropdownOpen,
  onMobileMenuClick,
  isSidebarCollapsed = false,
  onToggleSidebar
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { restartGuide } = useGuideStore();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const isTablet = useMediaQuery('(min-width: 769px) and (max-width: 1023px)');
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);

  useEffect(() => {
    let attempts = 0;
    const maxAttempts = 50;
    
    const checkWidget = setInterval(() => {
      if (window.FreshworksWidget) {
        setWidgetLoaded(true);
        clearInterval(checkWidget);
      }
      
      attempts++;
      if (attempts >= maxAttempts) {
        clearInterval(checkWidget);
      }
    }, 100);

    return () => clearInterval(checkWidget);
  }, []);

  const getInitials = (name: string | null | undefined) => {
    if (!name) return '';
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      toast.success(t('auth.logout.success'));
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error(t('auth.logout.error'));
    }
  };

  const openFreshworksWidget = (product: 'theme' | 'manager', shopifyUrl?: string) => {
    if (!widgetLoaded) {
      toast.error('Le widget de support est en cours de chargement. Veuillez réessayer dans quelques secondes.');
      return;
    }

    if (!window.FreshworksWidget) {
      toast.error('Le widget de support n\'est pas disponible. Veuillez réessayer plus tard.');
      return;
    }

    if (!user?.email) {
      toast.error('Vous devez être connecté pour créer un ticket.');
      return;
    }

    try {
      // Définir les champs à désactiver par défaut
      let fieldsToDisable = ['email', 'custom_fields.cf_produit_concern', 'custom_fields.cf_url_shopify'];
  
      // Ajouter des champs supplémentaires si "App Shopiweb Manager" est sélectionné
      if (product === 'manager') {
        fieldsToDisable = [
          ...fieldsToDisable,
          'custom_fields.cf_version',
          'custom_fields.cf_autorisation_daccs274321',
          'custom_fields.cf_code_collaborateur'
        ];
      }
  
      window.FreshworksWidget('disable', 'ticketForm', fieldsToDisable);
      
      // Pré-remplir les informations de l'utilisateur
      window.FreshworksWidget('prefill', 'ticketForm', {
        name: user.displayName || user.email.split('@')[0] || '',
        email: user.email,
        custom_fields: {
          cf_produit_concern: product === 'theme' ? 'Thème Shopiweb Premium' : 'App Shopiweb Manager',
          cf_url_shopify: shopifyUrl || ''
        }
      });

      // Fermer le modal de sélection
      setShowSupportModal(false);

      // Afficher et ouvrir le widget
      window.FreshworksWidget('show', 'launcher');
      window.FreshworksWidget('open');
    } catch (error) {
      console.error('Error opening Freshworks widget:', error);
      toast.error('Une erreur est survenue lors de l\'ouverture du widget de support.');
    }
  };

  const restartTutorial = () => {
    setIsDropdownOpen(false);
    restartGuide();
  };

  // Afficher le bouton de repli uniquement sur les écrans de bureau (pas sur mobile ou tablette)
  const showCollapseButton = !isMobile && !isTablet && onToggleSidebar;

  return (
    <nav className="bg-white dark:bg-zinc-900 shadow-lg">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center space-x-4">
            {isMobile ? (
              <button
                onClick={onMobileMenuClick}
                className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Menu className="w-6 h-6 text-gray-600 dark:text-gray-300" />
              </button>
            ) : (
              showCollapseButton && (
                <button
                  onClick={onToggleSidebar}
                  className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                >
                  {isSidebarCollapsed ? (
                    <ChevronRight className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                  ) : (
                    <ChevronLeft className="w-6 h-6 text-gray-600 dark:text-gray-300" />
                  )}
                </button>
              )
            )}
            <Link to="/" className="flex items-center space-x-2">
              <Logo className="w-8 h-8 text-gray-900 dark:text-white" />
              <span className="text-xl font-bold text-gray-900 dark:text-white hidden sm:inline">
                Shopiweb Manager
              </span>
            </Link>
          </div>

          <div className="flex items-center space-x-4">
            <NotificationCenter />

            <div className="relative" data-dropdown>
              <button
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <div className="relative">
                  {user?.photoURL ? (
                    <div className="w-10 h-10 rounded-full overflow-hidden ring-2 ring-offset-2 ring-blue-500 dark:ring-blue-400 dark:ring-offset-gray-800">
                      <img
                        src={user.photoURL}
                        alt={user.displayName || 'Profile'}
                        className="w-full h-full object-cover"
                        referrerPolicy="no-referrer"
                      />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center ring-2 ring-offset-2 ring-blue-500 dark:ring-blue-400 dark:ring-offset-gray-800">
                      <span className="text-blue-600 dark:text-blue-400 text-sm font-medium">
                        {getInitials(user?.displayName || user?.email)}
                      </span>
                    </div>
                  )}
                </div>
                <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              </button>

              {isDropdownOpen && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-xl shadow-lg py-1 border border-gray-100 dark:border-gray-700 z-50"
                >
                  <div className="px-4 py-2">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      {t('nav.profile')}
                    </p>
                  </div>

                  <Link
                    to="/profile"
                    onClick={() => setIsDropdownOpen(false)}
                    className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <User className="w-4 h-4 mr-2" />
                    {t('nav.profile')}
                  </Link>

                  <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      {t('nav.resources')}
                    </p>
                  </div>

                  <a
                    href="https://www.shopiweb.fr/#pricing"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsDropdownOpen(false)}
                    className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    {t('nav.buyLicense')}
                  </a>

                  <a
                    href="https://www.docs.shopiweb.fr/theme-premium"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => setIsDropdownOpen(false)}
                    className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {t('nav.documentation')}
                  </a>

                  <button
                    onClick={() => {
                      setIsDropdownOpen(false);
                      setShowSupportModal(true);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <LifeBuoy className="w-4 h-4 mr-2" />
                    {t('nav.ticket')}
                  </button>

                  {!isMobile && (
                    <button
                      onClick={restartTutorial}
                      id="reviewguide-button"
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                    >
                      <HelpCircle className="w-4 h-4 mr-2" />
                      {t('nav.reviewguide')}
                    </button>
                  )}

                  <div className="px-4 py-2 border-t border-gray-100 dark:border-gray-700">
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                      {t('nav.preferences')}
                    </p>
                  </div>

                  <UserSettings onSettingChange={() => setIsDropdownOpen(false)} />

                  <div className="border-t border-gray-100 dark:border-gray-700">
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/10"
                    >
                      <LogOut className="w-4 h-4 mr-2" />
                      {t('auth.logout.button')}
                    </button>
                  </div>
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {showSupportModal && (
          <SupportModal
            isOpen={showSupportModal}
            onClose={() => setShowSupportModal(false)}
            onProductSelect={openFreshworksWidget}
          />
        )}
      </AnimatePresence>
    </nav>
  );
};

export default Navbar;